"use client"

import { useEffect, useState } from "react"
import { observer } from "mobx-react-lite"
import { useAppContext } from "../../../shared/functions/Context"
import type { IUser } from "../../../shared/models/User"
import { TrendingDown, User, Building2, Briefcase, ChevronDown, ChevronUp, AlertTriangle, Activity } from "lucide-react"

interface RaterData {
  ratings: {
    [key: string]: { [key: string]: number | number[] }
  }
  stopDoing: string
  startDoing: string
  continueDoing: string
  type: "value" | "leadership"
}

interface GroupedRating {
  rateeId: string
  raterId: string
  raterData: RaterData
  date: string
}

interface GroupedRatings {
  [rateeId: string]: GroupedRating[]
}

const LowRated = observer(() => {
  const { api, store } = useAppContext()
  const [showDetails, setShowDetails] = useState<{ [key: string]: boolean }>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([api.user.getAll(), api.department.getAll(), api.ratingAssignments.getAll()])
        const users = store.user.all.map((user) => user.asJson)
        const inProgressAssignment = store.ratingAssignments.all.find(
          (rate) => rate.asJson.status === "In Progress",
        )?.asJson

        if (inProgressAssignment && users.length && inProgressAssignment.description) {
          const uids = users.map((user) => user.uid)
          await Promise.all([
            api.valueRating.getAllNew(uids, inProgressAssignment.description),
            api.leadershipRating.getAllNew(uids, inProgressAssignment.description),
          ])
        }
      } catch (error) {
        setError("An error occurred while fetching data. Please try again later.")
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [api, store])

  const toggleDetails = (rateeId: string, raterId: string) => {
    setShowDetails((prevState) => ({
      ...prevState,
      [`${rateeId}-${raterId}`]: !prevState[`${rateeId}-${raterId}`],
    }))
  }

  const getUserDetails = (userId: string): IUser | null => {
    return store.user.all.find((user) => user.asJson.uid === userId)?.asJson || null
  }

  const getDisplayNameById = (userId: string) => {
    return store.user.all.find((user) => user.asJson.uid === userId)?.asJson?.displayName
  }

  const getDepartmentName = (departmentId: string): string => {
    return store.department.all.find((department) => department.asJson.id === departmentId)?.asJson?.name || ""
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 w-full">
        <div className="relative">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-200"></div>
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-600 border-t-transparent absolute top-0 left-0"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <Activity className="h-5 w-5 text-red-600 animate-pulse" />
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64 w-full">
        <div className="text-center space-y-4">
          <div className="p-4 bg-red-100 rounded-full w-fit mx-auto">
            <TrendingDown className="h-8 w-8 text-red-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Error Loading Data</h3>
            <p className="text-slate-600">{error}</p>
          </div>
        </div>
      </div>
    )
  }

  const valueRatings = store.valueRating.all.map((rating) => rating.asJson)
  const leadershipRatings = store.leadershipRating.all.map((rating) => rating.asJson)

  const groupedRatings: GroupedRatings = {}

  // Group value ratings
  valueRatings.forEach((rating) => {
    Object.entries(rating.values).forEach(([raterId, raterData]) => {
      groupedRatings[rating.rateeId] = groupedRatings[rating.rateeId] || []
      groupedRatings[rating.rateeId].push({
        rateeId: rating.rateeId,
        raterId,
        raterData: { ...(raterData as RaterData), type: "value" },
        date: rating.date.toString(),
      })
    })
  })

  // Group leadership ratings
  leadershipRatings.forEach((rating) => {
    Object.entries(rating.criteria).forEach(([raterId, raterData]) => {
      groupedRatings[rating.rateeId] = groupedRatings[rating.rateeId] || []
      groupedRatings[rating.rateeId].push({
        rateeId: rating.rateeId,
        raterId,
        raterData: { ...(raterData as RaterData), type: "leadership" },
        date: rating.date.toString(),
      })
    })
  })

  const sortedRatings = Object.entries(groupedRatings)
    .map(([rateeId, ratings]) => {
      const totalOverallAverage = ratings.reduce((total, rating) => {
        const averages = Object.values(rating.raterData.ratings).map((statements) => {
          const values = Object.values(statements).flat()
          return values.reduce((sum, val) => sum + val, 0) / values.length
        })
        return total + averages.reduce((sum, avg) => sum + avg, 0) / averages.length
      }, 0)
      const finalOverallAverage = ratings.length > 0 ? totalOverallAverage / ratings.length : 0
      return { rateeId, ratings, finalOverallAverage }
    })
    .filter((rating) => rating.finalOverallAverage <= 3)
    .sort((a, b) => a.finalOverallAverage - b.finalOverallAverage)

  if (sortedRatings.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="p-4 bg-green-100 rounded-full w-fit mx-auto mb-4">
          <TrendingDown className="h-12 w-12 text-green-600" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">No Low Rated Employees</h3>
        <p className="text-slate-600">All employees are performing well with ratings above 3.0.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-red-100 rounded-lg">
          <AlertTriangle className="h-5 w-5 text-red-600" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Low Rated Employees</h2>
          <p className="text-slate-600">Employees with ratings of 3.0 and below requiring attention</p>
        </div>
      </div>

      {/* Results */}
      <div className="space-y-4">
        {sortedRatings.map(({ rateeId, ratings, finalOverallAverage }) => {
          const rateeName = getDisplayNameById(rateeId)
          const userDetails = getUserDetails(rateeId)
          const departmentName = userDetails ? getDepartmentName(userDetails.department) : ""

          return (
            <div key={rateeId} className="bg-white rounded-2xl shadow-lg border border-slate-200/50 overflow-hidden">
              {/* Employee Header */}
              <div className="bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 p-6 border-b border-red-100/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
                      <User className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-800">{rateeName}</h3>
                      <div className="flex items-center gap-4 text-sm text-slate-600 mt-1">
                        <div className="flex items-center gap-1">
                          <Building2 className="h-4 w-4" />
                          <span>{departmentName}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Briefcase className="h-4 w-4" />
                          <span>{userDetails?.jobTitle}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-2 mb-1">
                      <AlertTriangle className="h-5 w-5 text-red-500" />
                      <span className="text-2xl font-bold text-slate-800">{finalOverallAverage.toFixed(2)}</span>
                    </div>
                    <p className="text-sm text-slate-600">Overall Average</p>
                  </div>
                </div>
              </div>

              {/* Ratings Details */}
              <div className="p-6 space-y-4">
                {ratings.map(({ raterId, raterData, date }, index) => {
                  const raterName = getDisplayNameById(raterId)
                  const detailKey = `${rateeId}-${raterId}`
                  const isExpanded = showDetails[detailKey]

                  return (
                    <div
                      key={`${rateeId}-${raterId}-${index}`}
                      className="border border-slate-200 rounded-xl overflow-hidden"
                    >
                      <div className="p-4 bg-slate-50 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                            <User className="h-4 w-4 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">Rater: {raterName}</p>
                            <p className="text-sm text-slate-600">
                              Type: {raterData.type === "value" ? "Value" : "Leadership"}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => toggleDetails(rateeId, raterId)}
                          className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-white rounded-lg transition-colors duration-200"
                        >
                          {isExpanded ? "Hide Details" : "Show Details"}
                          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </button>
                      </div>

                      {isExpanded && (
                        <div className="p-4 bg-white border-t border-slate-200">
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b border-slate-200">
                                  <th className="text-left py-2 px-3 font-medium text-slate-700">Date</th>
                                  <th className="text-left py-2 px-3 font-medium text-slate-700">
                                    {raterData.type === "value" ? "Value Name" : "Leadership Criteria"}
                                  </th>
                                  <th className="text-left py-2 px-3 font-medium text-slate-700">Rating</th>
                                  <th className="text-left py-2 px-3 font-medium text-slate-700">Stop Doing</th>
                                  <th className="text-left py-2 px-3 font-medium text-slate-700">Start Doing</th>
                                  <th className="text-left py-2 px-3 font-medium text-slate-700">Continue Doing</th>
                                </tr>
                              </thead>
                              <tbody>
                                {Object.entries(raterData.ratings).map(([criteriaName, statements], criteriaIndex) => {
                                  const values = Object.values(statements).flat()
                                  const averageRating = values.reduce((sum, val) => sum + val, 0) / values.length

                                  return (
                                    <tr key={`${criteriaName}-${criteriaIndex}`} className="border-b border-slate-100">
                                      {criteriaIndex === 0 && (
                                        <td
                                          rowSpan={Object.keys(raterData.ratings).length}
                                          className="py-2 px-3 text-slate-600 align-top"
                                        >
                                          {new Date(date).toLocaleDateString()}
                                        </td>
                                      )}
                                      <td className="py-2 px-3 font-medium text-slate-800">{criteriaName}</td>
                                      <td className="py-2 px-3">
                                        <span
                                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                            averageRating <= 2
                                              ? "bg-red-100 text-red-800"
                                              : averageRating <= 3
                                                ? "bg-yellow-100 text-yellow-800"
                                                : "bg-green-100 text-green-800"
                                          }`}
                                        >
                                          {averageRating.toFixed(2)}
                                        </span>
                                      </td>
                                      {criteriaIndex === 0 && (
                                        <>
                                          <td
                                            rowSpan={Object.keys(raterData.ratings).length}
                                            className="py-2 px-3 text-slate-600 align-top"
                                          >
                                            {raterData.stopDoing}
                                          </td>
                                          <td
                                            rowSpan={Object.keys(raterData.ratings).length}
                                            className="py-2 px-3 text-slate-600 align-top"
                                          >
                                            {raterData.startDoing}
                                          </td>
                                          <td
                                            rowSpan={Object.keys(raterData.ratings).length}
                                            className="py-2 px-3 text-slate-600 align-top"
                                          >
                                            {raterData.continueDoing}
                                          </td>
                                        </>
                                      )}
                                    </tr>
                                  )
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
})

export default LowRated
